import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Printer, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import { treatsReportApi, DineInApiError, TreatsReport, TreatsPeriod } from '../../services/dineInApi';
import { athensDateTime, euro } from './registerFormat';

/**
 * «Ανά είδος» της καρτέλας «Κεράσματα & ακυρώσεις» του «Ταμείο» (πριν: παράθυρο «Κεράσματα» στο μενού,
 * GENERIS 5/10/2026): τι κεράστηκε — είδη, τεμάχια, αξία — με εκτύπωση στον εκτυπωτή αποδείξεων όπως
 * η Χ. Διάστημα: από το τελευταίο Ζ, ή ένα προηγούμενο κλείσιμο. Μετρούν και οι εκπτώσεις 100% (ο
 * ταμίας «κερνά» έτσι την ώρα της παραγγελίας). Ο server ελέγχει κατάστημα με τραπέζια και δικαίωμα
 * (backend/routes/dineInTreatsReport.js)· διακόπτης δεν υπάρχει από 12/10/2026.
 */

interface Props {
  storeId?: number;
  /** Αλλάζει με την ανανέωση του «Ταμείο» → ξαναφορτώνει το επιλεγμένο διάστημα. */
  refreshKey?: number;
}

/** «Τρέχουσα βάρδια (από 05/10 13:31)» ή «Ζ 04/10 23:58 (από 04/10 12:02)» — ώρα Ελλάδας. */
export function treatsPeriodLabel(period: TreatsPeriod): string {
  if (period.key === 'current' || !period.end) return `Τρέχουσα βάρδια (από ${athensDateTime(period.start)})`;
  return `Ζ ${athensDateTime(period.end)} (από ${athensDateTime(period.start)})`;
}

const TreatsReportPanel: React.FC<Props> = ({ storeId, refreshKey = 0 }) => {
  const [periodKey, setPeriodKey] = useState('current');
  const [periods, setPeriods] = useState<TreatsPeriod[]>([]);
  const [report, setReport] = useState<TreatsReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);
  const [printResult, setPrintResult] = useState<{ ok: boolean; message: string } | null>(null);

  const load = useCallback(async (key: string, alive: () => boolean) => {
    if (!storeId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await treatsReportApi.get(storeId, key);
      if (!alive()) return;
      setReport(data);
      setPeriods(data.periods || []);
    } catch (e) {
      if (!alive()) return;
      setReport(null);
      setError(e instanceof DineInApiError ? e.message : 'Η αναφορά δεν φορτώθηκε — δοκίμασε ξανά.');
    } finally {
      if (alive()) setLoading(false);
    }
  }, [storeId]);

  // Αλλαγή διαστήματος ή ανανέωση → φόρτωμα· η παλιότερη απάντηση που αργεί αγνοείται.
  useEffect(() => {
    let alive = true;
    load(periodKey, () => alive);
    return () => { alive = false; };
  }, [load, periodKey, refreshKey]);

  const choosePeriod = (key: string) => {
    setPeriodKey(key);
    setPrintResult(null);
  };

  const print = async () => {
    if (!storeId) return;
    setPrinting(true);
    setPrintResult(null);
    try {
      // Η απάντηση φέρνει και την αναφορά όπως τυπώθηκε (ίσως με νεότερα κεράσματα).
      const res = await treatsReportApi.print(storeId, periodKey);
      setReport(res);
      setPrintResult({ ok: res.printed === true, message: res.message });
    } catch (e) {
      setPrintResult({ ok: false, message: e instanceof DineInApiError ? e.message : 'Η εκτύπωση απέτυχε — δοκίμασε ξανά.' });
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1 min-w-0">
          {periods.length > 1 ? (
            <label className="block">
              <span className="text-xs font-semibold text-t3 uppercase tracking-wider">Διάστημα</span>
              <select
                data-testid="treats-period"
                value={periodKey}
                onChange={e => choosePeriod(e.target.value)}
                disabled={loading || printing}
                className="mt-1 w-full min-h-[48px] rounded-xl border border-line-strong bg-surface-2 px-3 py-2 text-base text-t1"
              >
                {periods.map(p => (
                  <option key={p.key} value={p.key}>{treatsPeriodLabel(p)}</option>
                ))}
              </select>
            </label>
          ) : report ? (
            <p className="text-sm text-t3">{treatsPeriodLabel(report.period)}</p>
          ) : null}
        </div>
        <button
          type="button"
          data-testid="treats-print"
          onClick={print}
          disabled={printing || loading || !report}
          className="min-h-[48px] px-5 rounded-xl bg-violet-600 hover:bg-violet-700 disabled:bg-surface-3 disabled:text-t3 disabled:cursor-not-allowed text-white font-semibold flex items-center justify-center gap-2 transition-all active:scale-[0.98] shrink-0"
        >
          {printing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Printer className="w-5 h-5" />}
          Εκτύπωση κερασμάτων
        </button>
      </div>

      {printResult && (
        <div
          role="status"
          className={`flex items-center gap-2 text-sm ${printResult.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-700 dark:text-red-300'}`}
        >
          {printResult.ok ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
          {printResult.message}
        </div>
      )}

      {loading && !report ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-7 h-7 text-violet-600 dark:text-violet-400 animate-spin" /></div>
      ) : error ? (
        <div role="alert" className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-700 dark:text-red-300 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      ) : report ? (
        report.lines.length === 0 ? (
          <p data-testid="treats-empty" className="text-center text-t3 text-sm py-8">Κανένα κέρασμα σε αυτό το διάστημα.</p>
        ) : (
          <div className={`bg-surface-2 dark:bg-surface-2/40 rounded-xl border border-line-strong/50 p-4 transition-opacity ${loading ? 'opacity-60' : ''}`}>
            <div className="flex items-center text-xs font-semibold text-t3 uppercase tracking-wider pb-2 border-b border-line-strong/50">
              <span className="flex-1">Είδος</span>
              <span className="w-16 text-right">Τεμ.</span>
              <span className="w-24 text-right">Αξία</span>
            </div>
            {report.lines.map(line => (
              <div
                key={`${line.productId ?? line.name}:${line.sizeId ?? ''}`}
                data-testid="treats-line"
                className="flex items-start text-sm text-t2 py-2 border-b border-line-strong/30 last:border-0"
              >
                <span className="flex-1 min-w-0 break-words">
                  {line.name}{line.size ? <span className="text-t3"> ({line.size})</span> : null}
                </span>
                <span className="w-16 text-right tabular-nums">{line.quantity}</span>
                <span className="w-24 text-right tabular-nums">{euro(line.value)}</span>
              </div>
            ))}
            <div className="flex items-center text-sm text-t1 font-bold pt-2 mt-1 border-t border-line-strong/50">
              <span className="flex-1">Σύνολο</span>
              <span data-testid="treats-total-quantity" className="w-16 text-right tabular-nums">{report.totals.quantity}</span>
              <span data-testid="treats-total-value" className="w-24 text-right tabular-nums">{euro(report.totals.value)}</span>
            </div>
            {(report.totals.discountQuantity || 0) > 0 && (
              <p data-testid="treats-discount" className="text-xs text-t2 mt-2">
                Με έκπτωση 100%: {report.totals.discountQuantity} τεμ. · {euro(report.totals.discountValue || 0)}
              </p>
            )}
            <p className="text-[11px] text-t3 mt-2">Αξία σε τιμές καταλόγου, πριν το κέρασμα ή την έκπτωση.</p>
          </div>
        )
      ) : null}
    </div>
  );
};

export default TreatsReportPanel;
